import React from 'react';
import { Button, Divider } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	compareStrings,
	getEntityById,
	isSupplierPayingCustomer,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import {
	createProposalSubContractor,
	proposalsRestCrudThunksForBuyer,
	proposalsRestCrudThunksForSupplier,
	proposalStatusChangeForContractor,
	proposalStatusChangeForSupplier,
} from '../../thunks/proposals_thunks';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import {
	getContractorQuote,
	getContractorQuotes,
	quotesClientRestCrudThunks,
	quotesSubcontractorRestCrudThunks,
	quotesRestCrudThunksForSupplier,
} from '../../thunks/quotes_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { isInternalTech, PERMISSION_NAMES } from '../../utils/AuthUtils';
import SupplierQuoteDetailDetailsPageWrapper from '../supplier_quote_detail_details_page_wrapper/SupplierQuoteDetailDetailsPageWrapper';
import ProposalAIForm from '../proposal_ai_form/ProposalAIForm';
import {
	getContractorWO,
	getSanitisedWorkOrder,
	isManagedBuyerCompanyIdWO,
} from '../../utils/WorkOrderUtils';
import { getWorkOrderSelector } from '../../selectors/workOrderSelectors';
import OWLink from '../OWLink';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';

require('./SupplierWorkOrdersDetailQuotesPage.less');

class SupplierWorkOrderQuotesDetailPage extends React.Component<any, any> {
	state = {
		noSupplier: false,
		quoteId: null,
		addingMarkup: false,
		editingQuote: false,
		loading: false,
		quoteExists: false,
	};

	getQuoteDetails = (workOrder) => {
		const { getContractorQuotes, getSubContractorQuotes, getQuotes, match, isClientView } =
			this.props;
		const workOrderId = match.params.id;
		const contractorWO = getContractorWO(workOrder, isClientView);
		const params = contractorWO
			? {
					contractorWorkOrderId: contractorWO.id,
					contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
			  }
			: { workOrderId, supplierFacilityId: workOrder.supplierFacilityId };

		const action = contractorWO
			? isClientView
				? getContractorQuotes
				: getSubContractorQuotes
			: getQuotes;
		action(params, 'workOrderAssociatedQuotes').then((quotes) => {
			this.setState({ quoteId: nullSafeGet('0.id', quotes) });
		});
	};

	refreshQuote = () => {
		const { getQuote, getWorkOrder, workOrder, isClientView, getCount, match, currentUser } =
			this.props;
		const quote = this.getQuote();
		const contractorWO = getContractorWO(workOrder, isClientView);
		const workOrderId = workOrder.id || nullSafeGet('params.id', match);
		if (isInternalTech(currentUser)) {
			getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		}
		if (contractorWO) {
			this.getQuoteDetails(workOrder);
			getWorkOrder(workOrder.id);
		} else {
			this.getQuoteDetails(workOrder);
			getWorkOrder(quote.workOrder.id);
		}
	};

	getQuote = () => {
		const { quotes } = this.props;
		return getEntityById(this.state.quoteId, quotes);
	};

	// After a quote is created via the embedded upload flow, re-fetch so the newly
	// created quote renders (which unmounts the uploader + drawer).
	refreshQuotesAfterCreate = () => {
		const { workOrder, currentUser, getCount, match } = this.props;
		const workOrderId = match.params.id;
		if (isInternalTech(currentUser)) {
			getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		}
		this.getQuoteDetails(workOrder);
	};

	componentDidMount() {
		const { getWorkOrder, match, workOrder, getCount, currentUser } = this.props;
		const workOrderId = match.params.id;
		if (isInternalTech(currentUser)) {
			getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		}

		if (workOrder && workOrder.id) {
			this.getQuoteDetails(workOrder);
		} else {
			getWorkOrder(workOrderId).then((data) => {
				this.getQuoteDetails(data);
			});
		}
	}

	render() {
		const {
			isClientView,
			quotesUpdating,
			quotesFetching,
			quotes,
			workOrder,
			currentUser,
			match,
			location,
		} = this.props;
		const workOrderId = match.params.id;
		const isInternalTech = nullSafeGetOrElse('facility.status', currentUser, '') === 'private';
		const isWorkOrderSupplierPrivate = compareStrings(
			nullSafeGet('supplierFacility.status', workOrder),
			'private'
		);

		const uploadQuoteHero = (
			<div className="mx-auto mt-[120px] flex max-w-[560px] flex-col items-center justify-center gap-6">
				<ProposalAIForm onCreated={this.refreshQuotesAfterCreate} />
				<Divider style={{ margin: 0 }}>
					<span className="text-character-secondary">OR</span>
				</Divider>
				<OWLink sameParent to={`/supplier/workOrders/detail/${workOrderId}/newProposal`}>
					<Button>Create from scratch</Button>
				</OWLink>
			</div>
		);
		const CreateQuoteButton = isWorkOrderSupplierPrivate ? null : isClientView || isInternalTech ? (
			isInternalTech ? (
				<AccessPermissionChecker name={PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER}>
					{uploadQuoteHero}
				</AccessPermissionChecker>
			) : (
				uploadQuoteHero
			)
		) : null;
		const quote = this.getQuote();
		const contractorWO = getContractorWO(workOrder, isClientView);
		const isSupplierManagedBuyer =
			(nullSafeGet('buyerCompanyId', workOrder) === -100 &&
				nullSafeGet('subContractedToWorkOrder.contractorSupplierFacilityId', workOrder) ===
					nullSafeGet('primaryFacilityId', currentUser)) ||
			isManagedBuyerCompanyIdWO(workOrder, currentUser);

		const isEmptyState = this.state.quoteExists && isInternalTech;

		if (isEmptyState) {
			return (
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
							alt=""
						/>
					}
					headline={
						!isInternalTech
							? "You haven't provided a quote for this work order yet."
							: "You haven't received a quote for this work order yet."
					}
					body={
						<span>
							Your service provider has drafted a quote, it just hasn't been published yet.
						</span>
					}
				/>
			);
		}

		//!isClientView meaning that they are approving it for subcontractor
		const canShowApprove =
			!isClientView || (isSupplierPayingCustomer(currentUser) && isSupplierManagedBuyer);
		const emptyStateFallback = (
			<EmptyState
				graphic={
					<img
						style={{ marginBottom: 8 }}
						src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter+owl.svg"
						alt=""
					/>
				}
				headline={
					isInternalTech || !isClientView
						? "You haven't received a quote for this work order yet."
						: "You haven't submitted a proposal yet."
				}
			/>
		);
		return (
			<AccessPermissionChecker name={PERMISSION_NAMES.CAN_VIEW_QUOTES} isPage>
				{quotesFetching ? (
					<PageLoadingPlaceholder />
				) : nullSafeGet('id', quote) ? (
					<SupplierQuoteDetailDetailsPageWrapper
						quote={quote}
						quotesUpdating={quotesUpdating}
						location={location}
						workOrder={getSanitisedWorkOrder(workOrder, isClientView)}
						refreshQuote={this.refreshQuote}
						showViewQuotePage={isClientView}
						isClientView={isClientView}
					/>
				) : CreateQuoteButton ? (
					isInternalTech ? (
						<AccessPermissionChecker
							name={PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER}
							exclude3rdPartySuppliers
							permissionDeniedContent={emptyStateFallback}
						>
							{CreateQuoteButton}
						</AccessPermissionChecker>
					) : (
						CreateQuoteButton
					)
				) : (
					emptyStateFallback
				)}
			</AccessPermissionChecker>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	quotesFetching: state.proposals.fetching,
	quotes: state.proposals,
	quote: state.proposals.detail,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	workOrder: getWorkOrderSelector(state, ownProps),
	workOrderFetching: state.work_orders.fetching,
	companyConfig: state.company_config.detail,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch) => ({
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	getQuotes: (params, targetCollectionName) =>
		dispatch(quotesClientRestCrudThunks.read(params, targetCollectionName)),
	getContractorQuotes: (params, targetCollectionName) =>
		dispatch(getContractorQuotes(params, targetCollectionName)),
	getQuote: (id) => dispatch(quotesRestCrudThunksForSupplier.readOne(id)),
	getContractorQuote: (id) => dispatch(getContractorQuote(id)),
	getSubContractorQuotes: (params, targetCollectionName) =>
		dispatch(quotesSubcontractorRestCrudThunks.read(params, targetCollectionName)),
	getSubContractorQuote: (id) => dispatch(quotesSubcontractorRestCrudThunks.readOne(id)),
	updateQuote: (entity) => dispatch(quotesRestCrudThunksForSupplier.update(entity)),
	createContractorProposal: (proposal) => dispatch(createProposalSubContractor(proposal)),
	createProposal: (proposal) => dispatch(proposalsRestCrudThunksForSupplier.create(proposal)),
	approveQuote: (entity) => dispatch(proposalStatusChangeForSupplier('awarded', entity)),
	declineQuote: (entity) => dispatch(proposalStatusChangeForSupplier('declined', entity)),
	approveQuoteContractor: (entity) =>
		dispatch(proposalStatusChangeForContractor('awarded', entity)),
	declineQuoteContractor: (entity) =>
		dispatch(proposalStatusChangeForContractor('declined', entity)),
	getCount: (woId, status) =>
		dispatch(quotesClientRestCrudThunks.countBy({ workOrderId: woId, status: status }, status)),
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierWorkOrderQuotesDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
